/**
 * Publishes the Pitch's player navigator (SE-8605) for the names drawn below
 * it — field card lines and Unassigned rows — under the hosting Pitch's
 * origin crumb.
 * @module features/scouting/contexts/PitchPlayerNavigationProvider
 */
import React, { memo, type ReactNode } from "react";

import { PitchPlayerNavigationContext } from "@/features/scouting/contexts/PitchPlayerNavigationContext";
import {
  type PitchPlayerOrigin,
  usePitchPlayerNavigation,
} from "@/features/scouting/hooks/usePitchPlayerNavigation";

/** Props for the Pitch's player-navigation provider. */
interface PitchPlayerNavigationProviderProps {
  /** Where the player page's back crumb leads. */
  readonly origin: PitchPlayerOrigin;
  readonly children: ReactNode;
}

/**
 * Makes every Pitch name below it a link to the player page.
 * @param props - The origin crumb and the Pitch subtree
 * @param props.origin - Where the player page's back crumb leads
 * @param props.children - The Pitch subtree
 * @returns The subtree under the navigator
 */
const PitchPlayerNavigationProvider = ({
  origin,
  children,
}: PitchPlayerNavigationProviderProps) => {
  const openPlayer = usePitchPlayerNavigation(origin);
  return (
    <PitchPlayerNavigationContext.Provider value={openPlayer}>
      {children}
    </PitchPlayerNavigationContext.Provider>
  );
};

PitchPlayerNavigationProvider.displayName = "PitchPlayerNavigationProvider";
export default memo(PitchPlayerNavigationProvider);
